import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { AITransactionFilter } from "@/components/AITransactionFilter";
import { CreateTransactionSheet } from "@/components/CreateTransactionSheet";
import { DateRangePicker, type DateRange } from "@/app/shared/ui/date-range-picker";
import { InlineTransactionTable } from "@/components/InlineTransactionTable";
import { LoanInterestRateHistoryComponent } from "./components/LoanInterestRateHistory";
import { TransactionFilters } from "@/components/TransactionFilters";
import { TransactionSortSelector } from "@/components/TransactionSortSelector";
import { UpdateTransactionSheet } from "@/components/UpdateTransactionSheet";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Pagination } from "@/app/shared/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Switch } from "@/app/shared/ui/switch";
import { Textarea } from "@groot/ui/textarea";
import { useCategories, useTags, useTransaction, useTransactions } from "@/hooks/api";
import { toast } from "@/lib/toast";
import { endOfMonthFormatted, formatDate, startOfMonthFormatted } from "@/lib/date.utils";
import { cn } from "@/lib/utils";
import { apiClient } from "@/services/api";
import {
  type Account,
  AccountType,
  type TransactionFilters as FilterType,
  BankSyncProvider,
  LoanInterestType,
  type TransactionType,
} from "@/types";
import dayjs from "dayjs";
import { ArrowLeft, ChevronDown, FileText, Pencil, Plus, Save, Search, X } from "lucide-react";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface FormData {
  accountName: string;
  type: AccountType;
  accountNumber: string;
  lastFourAccountNumber: string;
  previousLastFourAccountNumber: string; // Comma separated
  currentBalance: string;
  creditLimit: string;
  availableCreditLimit: string;
  lastStatementBalance: string;
  statementDayOfMonth: string;
  paymentDueDayOfMonth: string;
  currencyCode: string;
  slug: string;
  iconUrl: string;
  notes: string;
  interestRate: string;
  termInMonths: string;
  paymentStartDate: string;
  sortOrder: string;
  isArchived: boolean;
  syncWalletAppRecords: boolean;
  syncFoldMoneyRecords: boolean;
  walletAppAccountId: string;
  foldMoneyAccountId: string;
  currentBillingCycle: string;
  bankSyncEnabled: boolean;
  bankSyncProvider: string;
  bankSyncConfig: string; // JSON string
  meta: string; // JSON string
  loanInterestType: string;
}

export function AccountDetail() {
  const { id: accountIdParam } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState<Account | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isAddingInline, setIsAddingInline] = useState(false);

  // Transaction Filters State
  const [queryParams, setQueryParams] = useQueryStates({
    transactionId: parseAsInteger,
    startDate: parseAsString,
    endDate: parseAsString,
    categoryId: parseAsInteger,
    type: parseAsString,
    isReviewed: parseAsString,
    minAmount: parseAsInteger,
    maxAmount: parseAsInteger,
    notes: parseAsString,
    search: parseAsString,
    sortBy: parseAsString.withDefault("date"),
    sortOrder: parseAsString.withDefault("desc"),
    page: parseAsInteger.withDefault(1),
  });

  const PAGE_SIZE = 50;
  const currentPage = queryParams.page || 1;

  const filters: FilterType = {
    startDate: queryParams.startDate || startOfMonthFormatted(),
    endDate: queryParams.endDate || endOfMonthFormatted(),
    accountId: accountIdParam ? Number(accountIdParam) : undefined,
    categoryId: queryParams.categoryId || undefined,
    type: (queryParams.type as TransactionType) || undefined,
    isReviewed:
      queryParams.isReviewed === "true"
        ? true
        : queryParams.isReviewed === "false"
          ? false
          : undefined,
    minAmount: queryParams.minAmount || undefined,
    maxAmount: queryParams.maxAmount || undefined,
    search: queryParams.search || undefined,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    sortBy: (queryParams.sortBy as "date" | "amount" | "createdAt") || "date",
    sortOrder: (queryParams.sortOrder as "asc" | "desc") || "desc",
  };

  const { data: transactionsData, isLoading: transactionsLoading } = useTransactions(filters);
  const { data: categories = [] } = useCategories();
  const { data: tags = [] } = useTags();
  const { data: transactionDetail } = useTransaction(queryParams.transactionId || 0);

  const transactions = transactionsData?.transactions || [];
  const totalTransactions = transactionsData?.total || 0;
  const totalPages = Math.ceil(totalTransactions / PAGE_SIZE);

  // Actual-style search box, debounced into the `search` query param
  const [searchInput, setSearchInput] = useState(queryParams.search || "");

  useEffect(() => {
    setSearchInput(queryParams.search || "");
  }, [queryParams.search]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === (queryParams.search || "")) return;

    const timeout = setTimeout(() => {
      setQueryParams({ search: trimmed || null, page: 1 });
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput, queryParams.search, setQueryParams]);

  const handleColumnSort = (sortBy: "date" | "amount", sortOrder: "asc" | "desc") => {
    setQueryParams({ sortBy, sortOrder, page: 1 });
  };

  const handleAddNew = () => {
    // Desktop: inline spreadsheet-style add row; mobile: full create sheet
    if (window.matchMedia("(min-width: 768px)").matches) {
      setIsAddingInline(true);
    } else {
      setIsCreateSheetOpen(true);
    }
  };

  const [formData, setFormData] = useState<FormData>({
    accountName: "",
    type: AccountType.BANK,
    accountNumber: "",
    lastFourAccountNumber: "",
    previousLastFourAccountNumber: "",
    currentBalance: "0",
    creditLimit: "",
    availableCreditLimit: "",
    lastStatementBalance: "",
    statementDayOfMonth: "",
    paymentDueDayOfMonth: "",
    currencyCode: "INR",
    slug: "",
    iconUrl: "",
    notes: "",
    interestRate: "",
    termInMonths: "",
    paymentStartDate: "",
    sortOrder: "",
    isArchived: false,
    syncWalletAppRecords: false,
    syncFoldMoneyRecords: false,
    walletAppAccountId: "",
    foldMoneyAccountId: "",
    currentBillingCycle: "",
    bankSyncEnabled: false,
    bankSyncProvider: "",
    bankSyncConfig: "{}",
    meta: "{}",
    loanInterestType: "",
  });

  useEffect(() => {
    if (accountIdParam) {
      loadAccount();
    }
  }, [accountIdParam]);

  const loadAccount = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getAccount(Number(accountIdParam));
      setAccount(data);
      setFormData({
        accountName: data.accountName,
        type: data.type,
        accountNumber: data.accountNumber || "",
        lastFourAccountNumber: data.lastFourAccountNumber || "",
        previousLastFourAccountNumber: (data.previousLastFourAccountNumber || []).join(", "),
        currentBalance: data.currentBalance?.toString() || "0",
        creditLimit: data.creditLimit?.toString() || "",
        availableCreditLimit: data.availableCreditLimit?.toString() || "",
        lastStatementBalance: data.lastStatementBalance?.toString() || "",
        statementDayOfMonth: data.statementDayOfMonth?.toString() || "",
        paymentDueDayOfMonth: data.paymentDueDayOfMonth?.toString() || "",
        currencyCode: data.currencyCode || "INR",
        slug: data.slug,
        iconUrl: data.iconUrl || "",
        notes: data.notes || "",
        interestRate: data.interestRate?.toString() || "",
        termInMonths: data.termInMonths?.toString() || "",
        paymentStartDate: data.paymentStartDate
          ? dayjs(data.paymentStartDate).format("YYYY-MM-DD")
          : "",
        sortOrder: data.sortOrder?.toString() || "",
        isArchived: data.isArchived || false,
        syncWalletAppRecords: data.syncWalletAppRecords || false,
        syncFoldMoneyRecords: data.syncFoldMoneyRecords || false,
        walletAppAccountId: data.walletAppAccountId || "",
        foldMoneyAccountId: data.foldMoneyAccountId || "",
        currentBillingCycle: data.currentBillingCycle || "",
        bankSyncEnabled: data.bankSyncEnabled || false,
        bankSyncProvider: data.bankSyncProvider || "",
        bankSyncConfig: data.bankSyncConfig ? JSON.stringify(data.bankSyncConfig, null, 2) : "{}",
        meta: data.meta ? JSON.stringify(data.meta, null, 2) : "{}",
        loanInterestType: data.loanInterestType || "",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load account",
      });
      navigate("/accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.accountName.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Account name is required",
      });
      return;
    }

    if (formData.bankSyncEnabled && !formData.bankSyncProvider) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Bank Sync Provider is required when Bank Sync is enabled",
      });
      return;
    }

    let parsedMeta = {};
    try {
      parsedMeta = JSON.parse(formData.meta);
    } catch {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Invalid JSON in Meta field",
      });
      return;
    }

    let parsedBankSyncConfig = {};
    if (formData.bankSyncEnabled) {
      try {
        parsedBankSyncConfig = JSON.parse(formData.bankSyncConfig);
      } catch {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Invalid JSON in Bank Sync Config field",
        });
        return;
      }
    }

    try {
      setSaving(true);
      const payload: Partial<Account> = {
        accountName: formData.accountName.trim(),
        type: formData.type,
        slug: formData.slug.trim(),
        currencyCode: formData.currencyCode,
        currentBalance: formData.currentBalance ? Number.parseFloat(formData.currentBalance) : 0,
        accountNumber: formData.accountNumber || undefined,
        lastFourAccountNumber: formData.lastFourAccountNumber || undefined,
        previousLastFourAccountNumber: formData.previousLastFourAccountNumber
          ? formData.previousLastFourAccountNumber
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        creditLimit: formData.creditLimit ? Number.parseFloat(formData.creditLimit) : undefined,
        availableCreditLimit: formData.availableCreditLimit
          ? Number.parseFloat(formData.availableCreditLimit)
          : undefined,
        lastStatementBalance: formData.lastStatementBalance
          ? Number.parseFloat(formData.lastStatementBalance)
          : undefined,
        statementDayOfMonth: formData.statementDayOfMonth
          ? Number.parseInt(formData.statementDayOfMonth)
          : undefined,
        paymentDueDayOfMonth: formData.paymentDueDayOfMonth
          ? Number.parseInt(formData.paymentDueDayOfMonth)
          : undefined,
        iconUrl: formData.iconUrl || undefined,
        notes: formData.notes || undefined,
        interestRate: formData.interestRate ? Number.parseFloat(formData.interestRate) : undefined,
        termInMonths: formData.termInMonths ? Number.parseInt(formData.termInMonths) : undefined,
        paymentStartDate: formData.paymentStartDate || undefined,
        sortOrder: formData.sortOrder ? Number.parseInt(formData.sortOrder) : undefined,
        isArchived: formData.isArchived,
        syncWalletAppRecords: formData.syncWalletAppRecords,
        syncFoldMoneyRecords: formData.syncFoldMoneyRecords,
        walletAppAccountId: formData.walletAppAccountId || undefined,
        foldMoneyAccountId: formData.foldMoneyAccountId || undefined,
        currentBillingCycle: formData.currentBillingCycle || undefined,
        bankSyncEnabled: formData.bankSyncEnabled,
        bankSyncProvider: formData.bankSyncEnabled
          ? (formData.bankSyncProvider as BankSyncProvider)
          : undefined,
        bankSyncConfig: formData.bankSyncEnabled ? parsedBankSyncConfig : undefined,
        meta: parsedMeta,
        loanInterestType: formData.loanInterestType
          ? (formData.loanInterestType as LoanInterestType)
          : undefined,
      };

      await apiClient.updateAccount(Number(accountIdParam), payload);
      toast({
        title: "Success",
        description: "Account updated successfully",
      });
      setIsEditing(false);
      loadAccount();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update account",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 md:px-6">
        <LoadingSpinner size="lg" className="py-20" />
      </div>
    );
  }

  if (!account) {
    return null;
  }

  const getAccountIcon = () => {
    if (account.iconUrl) {
      return (
        <img
          src={account.iconUrl}
          alt={account.accountName}
          className="size-6 rounded object-contain flex-shrink-0"
        />
      );
    }
    return (
      <div className="size-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
        <span className="text-muted-foreground font-medium text-xs">
          {account.accountName.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  };

  return (
    <>
      {/* Full-width layout (like the Transactions page) — the shell skips
          container padding for this route, so sections pad themselves */}
      <div className="min-h-[calc(100vh-3.5rem)] bg-background">
        {/* Compact Actual-style header: back + name + balance + actions in one row */}
        <div className="px-4 md:px-6 py-3 border-b border-border flex flex-wrap items-center gap-x-3 gap-y-2">
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => navigate("/accounts")}
            aria-label="Back to accounts"
            className="-ml-1"
          >
            <ArrowLeft className="size-4" />
          </Button>
          {getAccountIcon()}
          <h1 className="text-lg font-medium text-foreground truncate">{account.accountName}</h1>
          <span className="text-sm text-muted-foreground hidden sm:inline">
            {account.type
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (l) => l.toUpperCase())}
            {account.lastFourAccountNumber ? ` • ${account.lastFourAccountNumber}` : ""}
            {account.isArchived ? " • Archived" : ""}
          </span>
          <span className="text-lg font-medium text-success">
            <AmountDisplay
              amount={account.currentBalance || 0}
              currencyCode={account.currencyCode}
            />
          </span>
          {!isEditing && (
            <div className="ml-auto flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/accounts/${account.id}/statements`)}
                className="gap-1.5"
              >
                <FileText className="size-4" />
                <span className="hidden md:inline">Statements</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetails((v) => !v)}
                className="gap-1.5"
                aria-expanded={showDetails}
              >
                Details
                <ChevronDown
                  className={cn("size-4 transition-transform", showDetails && "rotate-180")}
                />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="gap-1.5"
              >
                <Pencil className="size-4" />
                <span className="hidden md:inline">Edit</span>
              </Button>
            </div>
          )}
        </div>

        {!isEditing ? (
          <>
            {/* Collapsible account details — hidden by default to keep the top minimal */}
            {showDetails && (
              <div className="px-4 md:px-6 py-4 border-b border-border space-y-6">
                {/* Balance Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">Current Balance</p>
                    <p className="text-2xl font-medium text-foreground">
                      <AmountDisplay
                        amount={account.currentBalance || 0}
                        currencyCode={account.currencyCode}
                      />
                    </p>
                  </div>
                  {account.type === AccountType.CREDIT_CARD && (
                    <>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase mb-1">Credit Limit</p>
                        <p className="text-xl font-medium text-foreground">
                          <AmountDisplay
                            amount={account.creditLimit || 0}
                            currencyCode={account.currencyCode}
                          />
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase mb-1">Available</p>
                        <p className="text-xl font-medium text-foreground">
                          <AmountDisplay
                            amount={account.availableCreditLimit || 0}
                            currencyCode={account.currencyCode}
                          />
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Details Section */}
                <div>
                  <h2 className="text-sm font-medium text-foreground mb-4">Details</h2>
                  <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                    <div>
                      <dt className="text-sm text-muted-foreground">Account type</dt>
                      <dd className="text-sm text-foreground mt-1">
                        {account.type
                          .replace(/_/g, " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Currency</dt>
                      <dd className="text-sm text-foreground mt-1">{account.currencyCode}</dd>
                    </div>
                    {account.lastFourAccountNumber && (
                      <div>
                        <dt className="text-sm text-muted-foreground">Last 4 digits</dt>
                        <dd className="text-sm text-foreground font-mono mt-1">
                          {account.lastFourAccountNumber}
                        </dd>
                      </div>
                    )}
                    {account.notes && (
                      <div className="md:col-span-2">
                        <dt className="text-sm text-muted-foreground">Notes</dt>
                        <dd className="text-sm text-foreground mt-1">{account.notes}</dd>
                      </div>
                    )}
                    {account.type === AccountType.LOAN && (
                      <>
                        {account.loanInterestType && (
                          <div>
                            <dt className="text-sm text-muted-foreground">Interest Type</dt>
                            <dd className="text-sm text-foreground mt-1 capitalize">
                              {account.loanInterestType}
                            </dd>
                          </div>
                        )}
                        {account.loanInterestType === LoanInterestType.FIXED &&
                          account.interestRate && (
                            <div>
                              <dt className="text-sm text-muted-foreground">Interest Rate</dt>
                              <dd className="text-sm text-foreground mt-1">
                                {account.interestRate}%
                              </dd>
                            </div>
                          )}
                        {account.termInMonths && (
                          <div>
                            <dt className="text-sm text-muted-foreground">Term</dt>
                            <dd className="text-sm text-foreground mt-1">
                              {account.termInMonths} months
                            </dd>
                          </div>
                        )}
                      </>
                    )}
                  </dl>
                </div>

                {/* Interest Rate History for floating rate loans only */}
                {account.type === AccountType.LOAN &&
                  account.loanInterestType === LoanInterestType.FLOATING && (
                    <div className="pt-4 border-t border-border">
                      <LoanInterestRateHistoryComponent accountId={account.id} />
                    </div>
                  )}
              </div>
            )}

            {/* Transactions Section — no heading; search lives in the toolbar */}
            <div className="px-4 md:px-6 py-4">
              {/* Toolbar: Add New, Filter, sort, search, date range (Actual-style) */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap mb-3">
                <Button size="sm" variant="outline" className="gap-1.5 h-7" onClick={handleAddNew}>
                  <Plus className="size-3.5" />
                  Add New
                </Button>
                <TransactionFilters
                  filters={filters}
                  onFiltersChange={(newFilters) => {
                    setQueryParams({
                      startDate: newFilters.startDate || null,
                      endDate: newFilters.endDate || null,
                      categoryId: newFilters.categoryId || null,
                      type: newFilters.type || null,
                      isReviewed:
                        newFilters.isReviewed !== undefined ? String(newFilters.isReviewed) : null,
                      minAmount: newFilters.minAmount || null,
                      maxAmount: newFilters.maxAmount || null,
                      notes: queryParams.notes || null,
                      page: 1,
                    });
                  }}
                  accounts={[account]}
                  categories={categories}
                  excludeFields={["account"]}
                />
                <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
                  <div className="md:hidden">
                    <TransactionSortSelector
                      value={filters.sortOrder || "desc"}
                      onValueChange={(order) => setQueryParams({ sortOrder: order, page: 1 })}
                    />
                  </div>
                  <AITransactionFilter
                    onFilterApply={(newFilters) => {
                      setQueryParams({
                        startDate: newFilters.startDate || null,
                        endDate: newFilters.endDate || null,
                        categoryId: newFilters.categoryId || null,
                        type: newFilters.type || null,
                        isReviewed:
                          newFilters.isReviewed !== undefined
                            ? String(newFilters.isReviewed)
                            : null,
                        minAmount: newFilters.minAmount || null,
                        maxAmount: newFilters.maxAmount || null,
                        notes: queryParams.notes || null,
                        page: 1,
                      });
                    }}
                    accounts={[account]}
                    categories={categories}
                  />
                  <DateRangePicker
                    className="w-full sm:w-auto"
                    value={{
                      from: filters.startDate ? dayjs(filters.startDate).toDate() : undefined,
                      to: filters.endDate ? dayjs(filters.endDate).toDate() : undefined,
                    }}
                    onChange={(dateRange?: DateRange) => {
                      if (!dateRange?.from || !dateRange?.to) return;
                      setQueryParams({
                        startDate: formatDate(dateRange.from),
                        endDate: formatDate(dateRange.to),
                        page: 1,
                      });
                    }}
                  />
                  {/* Actual-style search box */}
                  <div className="relative w-full sm:w-48">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search"
                      className="h-8 pl-8 pr-7 text-sm"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={() => setSearchInput("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label="Clear search"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Table (Actual-style: bare, no card wrapper so editors overlay cleanly) */}
              <InlineTransactionTable
                transactions={transactions}
                accounts={[account]}
                categories={categories}
                tags={tags}
                isLoading={transactionsLoading}
                onEdit={(transaction) => setQueryParams({ transactionId: transaction.id })}
                showAccountColumn={false}
                sortBy={filters.sortBy}
                sortOrder={filters.sortOrder}
                onSort={handleColumnSort}
                isAdding={isAddingInline}
                onCancelAdd={() => setIsAddingInline(false)}
                defaultAccountId={account.id}
              />

              {/* Pagination */}
              {totalTransactions > PAGE_SIZE && (
                <div className="py-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalTransactions}
                    itemsPerPage={PAGE_SIZE}
                    onPageChange={(page) => setQueryParams({ page })}
                    isLoading={transactionsLoading}
                    itemName="transactions"
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 mx-auto max-w-5xl px-4 md:px-6 py-6">
            <div className="border border-border rounded-lg p-6">
              <h2 className="text-sm font-medium text-foreground mb-4">Basic information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="accountName">Account name *</Label>
                  <Input
                    id="accountName"
                    value={formData.accountName}
                    onChange={(e) => handleChange("accountName", e.target.value)}
                    className="border-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Account type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleChange("type", value as AccountType)}
                  >
                    <SelectTrigger id="type" className="border-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AccountType.BANK}>Bank</SelectItem>
                      <SelectItem value={AccountType.CREDIT_CARD}>Credit Card</SelectItem>
                      <SelectItem value={AccountType.CASH}>Cash</SelectItem>
                      <SelectItem value={AccountType.LOAN}>Loan</SelectItem>
                      <SelectItem value={AccountType.DIGITAL_WALLET}>Digital Wallet</SelectItem>
                      <SelectItem value={AccountType.CRYPTOCURRENCY}>Cryptocurrency</SelectItem>
                      <SelectItem value={AccountType.OTHER}>Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleChange("slug", e.target.value)}
                    className="border-input"
                    pattern="[a-z0-9\-]+"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currencyCode">Currency</Label>
                  <Input
                    id="currencyCode"
                    value={formData.currencyCode}
                    onChange={(e) => handleChange("currencyCode", e.target.value)}
                    className="border-input"
                    maxLength={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="iconUrl">Icon URL</Label>
                  <Input
                    id="iconUrl"
                    type="url"
                    value={formData.iconUrl}
                    onChange={(e) => handleChange("iconUrl", e.target.value)}
                    className="border-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sortOrder">Sort Order</Label>
                  <Input
                    id="sortOrder"
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => handleChange("sortOrder", e.target.value)}
                    className="border-input"
                  />
                </div>
              </div>
            </div>

            <div className="border border-border rounded-lg p-6">
              <h2 className="text-sm font-medium text-foreground mb-4">Account Identifiers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="accountNumber">Account Number</Label>
                  <Input
                    id="accountNumber"
                    value={formData.accountNumber}
                    onChange={(e) => handleChange("accountNumber", e.target.value)}
                    placeholder="Full account number"
                    className="border-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastFourAccountNumber">Last 4 digits</Label>
                  <Input
                    id="lastFourAccountNumber"
                    value={formData.lastFourAccountNumber}
                    onChange={(e) => handleChange("lastFourAccountNumber", e.target.value)}
                    className="border-input"
                    maxLength={4}
                    pattern="[0-9]{4}"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="previousLastFourAccountNumber">
                    Previous Last 4 Digits (comma separated)
                  </Label>
                  <Input
                    id="previousLastFourAccountNumber"
                    value={formData.previousLastFourAccountNumber}
                    onChange={(e) => handleChange("previousLastFourAccountNumber", e.target.value)}
                    placeholder="1234, 5678"
                    className="border-input"
                  />
                </div>
              </div>
            </div>

            <div className="border border-border rounded-lg p-6">
              <h2 className="text-sm font-medium text-foreground mb-4">Financial details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="currentBalance">Current balance</Label>
                  <Input
                    id="currentBalance"
                    type="number"
                    step="0.01"
                    value={formData.currentBalance}
                    onChange={(e) => handleChange("currentBalance", e.target.value)}
                    className="border-input"
                  />
                </div>

                {formData.type === AccountType.CREDIT_CARD && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="creditLimit">Credit limit</Label>
                      <Input
                        id="creditLimit"
                        type="number"
                        step="0.01"
                        value={formData.creditLimit}
                        onChange={(e) => handleChange("creditLimit", e.target.value)}
                        className="border-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="availableCreditLimit">Available credit</Label>
                      <Input
                        id="availableCreditLimit"
                        type="number"
                        step="0.01"
                        value={formData.availableCreditLimit}
                        onChange={(e) => handleChange("availableCreditLimit", e.target.value)}
                        className="border-input"
                      />
                    </div>
                  </>
                )}

                {formData.type === AccountType.LOAN && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="loanInterestType">Interest Type</Label>
                      <Select
                        value={formData.loanInterestType}
                        onValueChange={(value) => handleChange("loanInterestType", value)}
                      >
                        <SelectTrigger id="loanInterestType" className="border-input">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={LoanInterestType.FIXED}>Fixed</SelectItem>
                          <SelectItem value={LoanInterestType.FLOATING}>Floating</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="termInMonths">Term (months)</Label>
                      <Input
                        id="termInMonths"
                        type="number"
                        value={formData.termInMonths}
                        onChange={(e) => handleChange("termInMonths", e.target.value)}
                        className="border-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="paymentStartDate">Payment Start Date</Label>
                      <Input
                        id="paymentStartDate"
                        type="date"
                        value={formData.paymentStartDate}
                        onChange={(e) => handleChange("paymentStartDate", e.target.value)}
                        className="border-input"
                      />
                    </div>

                    {formData.loanInterestType === LoanInterestType.FIXED && (
                      <div className="space-y-2">
                        <Label htmlFor="interestRate">Interest Rate (%)</Label>
                        <Input
                          id="interestRate"
                          type="number"
                          step="0.01"
                          placeholder="8.50"
                          value={formData.interestRate}
                          onChange={(e) => handleChange("interestRate", e.target.value)}
                          className="border-input"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="border border-border rounded-lg p-6">
              <h2 className="text-sm font-medium text-foreground mb-4">Bank Sync Configuration</h2>
              <div className="space-y-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="bankSyncEnabled"
                    checked={formData.bankSyncEnabled}
                    onCheckedChange={(checked) => handleChange("bankSyncEnabled", checked)}
                  />
                  <Label htmlFor="bankSyncEnabled">Enable Bank Sync</Label>
                </div>

                {formData.bankSyncEnabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="bankSyncProvider">Provider</Label>
                      <Select
                        value={formData.bankSyncProvider}
                        onValueChange={(value) => handleChange("bankSyncProvider", value)}
                      >
                        <SelectTrigger id="bankSyncProvider" className="border-input">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={BankSyncProvider.FOLD_MONEY}>Fold Money</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bankSyncConfig">Configuration (JSON)</Label>
                      <Textarea
                        id="bankSyncConfig"
                        value={formData.bankSyncConfig}
                        onChange={(e) => handleChange("bankSyncConfig", e.target.value)}
                        placeholder="{}"
                        className="border-input font-mono text-[0.9em]"
                        rows={4}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="border border-border rounded-lg p-6">
              <h2 className="text-sm font-medium text-foreground mb-4">Additional settings</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="isArchived">Archive account</Label>
                  <Switch
                    id="isArchived"
                    checked={formData.isArchived}
                    onCheckedChange={(checked) => handleChange("isArchived", checked)}
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label htmlFor="meta">Meta Data (JSON)</Label>
                <Textarea
                  id="meta"
                  value={formData.meta}
                  onChange={(e) => handleChange("meta", e.target.value)}
                  placeholder="{}"
                  className="border-input font-mono text-[0.9em]"
                  rows={4}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                className="border-input"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  loadAccount();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <div className="size-4 animate-spin rounded-full border-2 border-background border-t-foreground" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save changes
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
      {/* Create Transaction Sheet */}
      {account && (
        <CreateTransactionSheet
          accounts={[account]}
          categories={categories}
          tags={tags}
          isOpen={isCreateSheetOpen}
          onOpenChange={setIsCreateSheetOpen}
          defaultAccountId={account.id}
        />
      )}

      {/* Edit Transaction Sheet */}
      {account && (
        <UpdateTransactionSheet
          transaction={transactionDetail || null}
          accounts={[account]}
          categories={categories}
          tags={tags}
          isOpen={!!queryParams.transactionId}
          onOpenChange={(open) => {
            if (!open) setQueryParams({ transactionId: null });
          }}
          onUpdate={() => {
            setQueryParams({ transactionId: null });
          }}
        />
      )}
    </>
  );
}
